import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, Clock, MapPin, Mail, User } from 'lucide-react';

console.log('🟢 NameClaimPage.js file loaded');

const NameClaimPage = () => {
  console.log('🟢 NameClaimPage component rendering...');
  
  const { claimCode } = useParams();
  const navigate = useNavigate();
  
  console.log('🔍 NameClaimPage loaded with claimCode:', claimCode);
  console.log('🔍 Current location:', window.location.href);
  
  const [nameRequest, setNameRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  const [formData, setFormData] = useState({
    // User details
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    // Group details
    groupName: '',
    groupDescription: '',
    groupLocation: ''
  });

  useEffect(() => {
    fetchNameRequest();
  }, [claimCode]);

  const fetchNameRequest = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching name request for claimCode:', claimCode);
      
      const response = await fetch(`/api/names/${claimCode}`);
      console.log('🔍 API Response status:', response.status);
      
      const data = await response.json();
      console.log('🔍 API Response data:', data);
      
      if (data.success) {
        setNameRequest(data.nameRequest);
        console.log('✅ Name request loaded:', data.nameRequest);
        // Pre-fill some form data
        setFormData(prev => ({
          ...prev,
          groupName: data.nameRequest.suggested_community_name || '',
          groupLocation: data.nameRequest.suggested_location || ''
        }));
      } else {
        console.error('❌ API Error:', data.error);
        setError(data.error || 'Invalid or expired request');
      }
    } catch (error) {
      console.error('❌ Network Error:', error);
      setError('Unable to load request. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyClaimCode = async () => {
    try {
      await navigator.clipboard.writeText(claimCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = claimCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.groupName) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await fetch(`/api/names/${claimCode}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userDetails: {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            phone: formData.phone
          },
          groupDetails: {
            name: formData.groupName,
            description: formData.groupDescription,
            location: formData.groupLocation
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        // Redirect to dashboard or success page
        navigate(`/group/${data.group.id}?welcome=true&nameAdded=true`);
      } else {
        setError(data.error || 'Failed to create account');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const daysUntilExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper to parse recipient name (handles JSON arrays from database)
  const parseRecipientName = (name) => {
    if (!name) return '';
    
    // If it's already a string without brackets, return as-is
    if (typeof name === 'string' && !name.startsWith('[')) {
      return name;
    }
    
    // Try to parse if it looks like JSON
    try {
      const parsed = JSON.parse(name);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return name;
    } catch (e) {
      // Not JSON, return as-is
      return name;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error && !nameRequest) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Not Available</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Yesod Logo - Header */}
          <div className="text-center mb-6">
            <img 
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
              alt="Yesod" 
              className="h-10 mx-auto"
            />
          </div>

          {/* Account Creation Form - Matching the invitation style */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            
            {/* Header */}
            <div className="text-center pt-8 pb-6 border-b border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-700 text-xl">🕍</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Create Your Profile</h2>
              <p className="text-gray-600 mt-2">Accept "{parseRecipientName(nameRequest?.prayer_recipient_name)}" to your prayer list</p>
            </div>

            {/* Form */}
            <div className="p-8">
              <form onSubmit={handleCreateAccount} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Rabbi David Cohen"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="rabbi@synagogue.org"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Community Name
                  </label>
                  <input
                    type="text"
                    name="groupName"
                    value={formData.groupName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Beth Shalom Synagogue"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="groupLocation"
                    value={formData.groupLocation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="New York, NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Create Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Choose a secure password"
                    minLength="6"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Confirm your password"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-4 pt-4">
                  <button
                    type="submit"
                    disabled={creating}
                    className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
                  >
                    {creating ? 'Creating Profile...' : 'Accept Name & Complete Setup'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="w-full text-gray-600 font-medium py-3 px-6 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Back to Request
                  </button>
                </div>
              </form>

              {/* Footer Note */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  By creating an account, you'll automatically accept this name to your prayer list
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Yesod Logo - Header */}
        <div className="text-center mb-6">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-12 mx-auto"
          />
        </div>

        {/* Simplified Digital Invitation Layout */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          
          {/* Visual Header - Centered Avatar */}
          <div className="text-center pt-8 pb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
              {nameRequest.avatar_url ? (
                <img 
                  src={nameRequest.avatar_url} 
                  alt={(nameRequest.user_first_name !== null && nameRequest.user_first_name !== undefined) || (nameRequest.user_last_name !== null && nameRequest.user_last_name !== undefined) ? `${nameRequest.user_first_name || ''} ${nameRequest.user_last_name || ''}`.trim() : 'Sender'} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-blue-700 font-semibold text-xl">
                  {nameRequest.user_first_name 
                    ? nameRequest.user_first_name.charAt(0).toUpperCase() 
                    : '?'}
                </div>
              )}
            </div>
            <p className="text-gray-900 text-lg font-bold mb-1">
              {(nameRequest.user_first_name !== null && nameRequest.user_first_name !== undefined) || (nameRequest.user_last_name !== null && nameRequest.user_last_name !== undefined)
                ? `${nameRequest.user_first_name || ''} ${nameRequest.user_last_name || ''}`.trim()
                : 'Anonymous'}
            </p>
            <p className="text-gray-600 text-sm">
              Requested to add a name to your Shul's prayer list
            </p>
          </div>

          {/* Hero Content - The Name (Big Ticket) */}
          <div className="px-8 pb-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center mb-8">
              {/* Status Badge - Pending */}
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                  <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
                  Waiting for approval
                </span>
              </div>
              
              <div className="flex items-center justify-center mb-2">
                <span className="text-3xl mr-3">📖</span>
                <h2 className="text-2xl font-bold text-gray-900">
                  {parseRecipientName(nameRequest.prayer_recipient_name)}
                </h2>
              </div>
              <p className="text-gray-600 text-lg font-medium mb-4">Misheberach List</p>
              
              {/* Expiry Notice - Inside Card with Urgency */}
              {nameRequest.expires_at && (
                <div className="flex items-center justify-center gap-1.5 text-sm text-orange-600 mb-4">
                  <Clock className="h-4 w-4" />
                  <span>Expires in {daysUntilExpiry(nameRequest.expires_at)} days</span>
                </div>
              )}
              
              {nameRequest.request_message && (
                <div className="mt-6 pt-6 border-t border-blue-100">
                  <p className="text-gray-700 italic leading-relaxed">
                    "{nameRequest.request_message}"
                  </p>
                </div>
              )}
            </div>

            {/* Instruction Text */}
            <p className="text-gray-600 text-sm text-center mb-4">
              To accept this request, you need to verify your community details.
            </p>

            {/* Single Action Button */}
            <button
              onClick={() => navigate(`/name-claim-signup?claim=${claimCode}&name=${encodeURIComponent(parseRecipientName(nameRequest.prayer_recipient_name))}&type=name`)}
              className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors shadow-sm text-lg"
            >
              Setup Shul & Accept Name
            </button>

            {/* Minimal Footer Info */}
            <div className="text-center mt-6 text-sm text-gray-500">
              <p>Ref #{claimCode}</p>
            </div>
          </div>
        </div>

        {/* Subtle Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-400 text-xs">
            Powered by <span className="font-medium text-gray-500">Yesod</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NameClaimPage;